/**
 * What `vf-tiptap-editor` does with a file pasted or dropped into it: the FileDropHandler plugin (a paste, or a drop on the
 * text) and the shell drop zone (`onShellDrop` / `ingestExternalFile`, a drop on the editor's chrome) all end in
 * `_handleFileDropped`, which reads `fileDropMode`. Pure, so file-drop-mode.spec.ts pins it.
 *
 *  - `'inline'` (the default, every host that binds nothing: the task notes, the task modal's comment composer, ...): the
 *    editor's own upload modal (`vf-image-upload-modal`) saves the file and a reference token is inserted at the drop point
 *    (at the caret for a paste). Unchanged.
 *  - `'host'`: the editor still validates the files (empty, over the size cap: the same toasts) and names a pasted image, then
 *    hands EVERY accepted file of the paste or drop to its host in one `(fileDrop)` (`splitHostFileDrop`); the editor neither
 *    uploads nor inserts anything. The Global Conversations
 *    overlay's comment composers use it, so a dropped or pasted file becomes an attachment chip through the composer's Attach
 *    upload step (comment-composer.component.ts `dropFilesAsAttachments`), not a link in the text.
 */

/** The file-drop modes of `vf-tiptap-editor`. */
export type TiptapFileDropMode = 'inline' | 'host';

/** The mode of an editor whose host binds nothing: the upload modal and an inline reference token, as always. */
export const TIPTAP_DEFAULT_FILE_DROP_MODE: TiptapFileDropMode = 'inline';

/** The drop card's second line in `'inline'` mode (unchanged). */
export const FILE_DROP_HINT_INLINE = 'Inserted at cursor position';

/**
 * The drop card's second line in `'host'` mode: the file goes to the composer's upload step (it is not put in the text). It
 * promises no outcome, because the reader still confirms the save location there and may cancel the step (where the file is
 * saved depends on the composer: in 'project' sources mode the project folder, or OneDrive when the project has none, SD3-19
 * amended 2026-10-20).
 */
export const FILE_DROP_HINT_HOST = 'Opens the upload step';

/** True only for `'host'`; anything else (unbound, null, unknown) is the default `'inline'`. */
export function handsFileDropToHost(mode: TiptapFileDropMode | null | undefined): boolean {
    return mode === 'host';
}

/** The drop card's second line for a mode. */
export function fileDropHint(mode: TiptapFileDropMode | null | undefined): string {
    return handsFileDropToHost(mode) ? FILE_DROP_HINT_HOST : FILE_DROP_HINT_INLINE;
}

/** Why the editor refuses a pasted or dropped file: the same two checks FileDropHandler makes on a single file. */
export type HostFileDropRefusal = 'empty' | 'too-large';

/** The files of one `'host'` paste or drop, split by the editor's checks. */
export interface HostFileDropSplit<T> {
    /** Handed to the host in one `(fileDrop)`, in the order they came. */
    accepted: T[];
    /** Refused, each with its reason (one toast each, as for a single file). */
    refused: { file: T; reason: HostFileDropRefusal }[];
}

/**
 * EVERY file of a `'host'` paste or drop, split by the editor's checks: not empty, and not above `maxBytes`. `'inline'` still
 * takes the first file only (its upload modal takes one); `'host'` hands the host all of them, because the composer's upload
 * step takes a list, so dropping or pasting several files attaches several. A missing entry is skipped (it has no name to
 * report).
 */
export function splitHostFileDrop<T extends { size: number }>(
    files: ArrayLike<T | null | undefined> | null | undefined,
    maxBytes: number
): HostFileDropSplit<T> {
    const accepted: T[] = [];
    const refused: { file: T; reason: HostFileDropRefusal }[] = [];

    if (!files) {
        return { accepted, refused };
    }
    for (let i = 0; i < files.length; i++) {
        const file: T | null | undefined = files[i];

        if (!file) {
            continue;
        }
        if (!file.size) {
            refused.push({ file, reason: 'empty' });
            continue;
        }
        if (file.size > maxBytes) {
            refused.push({ file, reason: 'too-large' });
            continue;
        }
        accepted.push(file);
    }
    return { accepted, refused };
}
